import { computed, readonly, shallowRef, toRef, watch, type MaybeRefOrGetter } from 'vue'
import { useVideoCall } from '@/composables/useVideoCall'
import type { Session, Student } from '@/types/global'

export type StudentCallFlowState =
  | 'idle'
  | 'ringing'
  | 'joining'
  | 'connected'
  | 'leaving'
  | 'failed'

type UseStudentCallFlowOptions = {
  callEvent?: MaybeRefOrGetter<Student['call_event'] | undefined>
  canJoinCall?: MaybeRefOrGetter<boolean>
  isVideoCallOpen?: MaybeRefOrGetter<boolean>
}

const state = shallowRef<StudentCallFlowState>('idle')
const error = shallowRef<string | null>(null)

export function useStudentCallFlow(options: UseStudentCallFlowOptions = {}) {
  const { joinStudentVideoCall, closeVideoCall, videoCallId } = useVideoCall()

  if (
    options.callEvent !== undefined &&
    options.canJoinCall !== undefined &&
    options.isVideoCallOpen !== undefined
  ) {
    watch(
      [toRef(options.callEvent), toRef(options.canJoinCall), toRef(options.isVideoCallOpen)],
      ([callEvent, canJoinCall, isVideoCallOpen]) => {
        if (callEvent?.event === 'call_ended') {
          // A teacher/moderator can end the private room while this student is
          // connected. Close the modal here (the single Agora owner) so its
          // show watcher leaves the channel and releases all local tracks.
          // Legacy events without a room ID still apply to this student/session.
          const endedCallID = callEvent.session_call_id
          if (!endedCallID || !videoCallId.value || videoCallId.value === endedCallID) {
            closeVideoCall()
          }
          error.value = null
          state.value = 'idle'
          return
        }

        if (isVideoCallOpen) return

        const isJoinable =
          (callEvent?.event === 'call_ring' || callEvent?.event === 'call_started') &&
          Boolean(canJoinCall)

        if (isJoinable && state.value === 'idle') {
          error.value = null
          state.value = 'ringing'
          return
        }

        if (!isJoinable && (state.value === 'ringing' || state.value === 'failed')) {
          error.value = null
          state.value = 'idle'
        }
      },
      { immediate: true }
    )
  }

  function answer(student: Student, session: Session, openModal = true) {
    if (state.value !== 'ringing' && state.value !== 'failed') return false

    error.value = null
    state.value = 'joining'
    if (openModal) joinStudentVideoCall(student, session)
    return true
  }

  function markConnected() {
    error.value = null
    state.value = 'connected'
  }

  function markLeaving() {
    if (state.value === 'connected') {
      state.value = 'leaving'
    }
  }

  function markFailed(message: string) {
    error.value = message
    state.value = 'failed'
  }

  function markIdle() {
    error.value = null
    state.value = 'idle'
  }

  return {
    state: readonly(state),
    error: readonly(error),
    isIncomingCallVisible: computed(
      () => state.value === 'ringing' || state.value === 'joining' || state.value === 'failed'
    ),
    isAnswering: computed(() => state.value === 'joining'),
    isConnected: computed(() => state.value === 'connected'),
    answer,
    markConnected,
    markLeaving,
    markFailed,
    markIdle
  }
}
